import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { adminRequest } from '../../utils/adminRequest';

const baseURL = process.env.REACT_APP_API_URL || (typeof window !== 'undefined' ? window.location.origin : '');

/** 从富文本 HTML 中提取第一张图片的 src */
function extractFirstImageSrc(html) {
  if (!html || typeof html !== 'string') return null;
  const m = html.match(/<img[^>]+src=["']([^"']+)["']/i);
  return m ? m[1] : null;
}

function AdminActivityForm() {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const imageHandler = function () {
    const quill = this.quill;
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      const fd = new FormData();
      fd.append('file', file);
      try {
        const token = typeof window !== 'undefined' && window.localStorage?.getItem?.('admin_token');
        const res = await fetch(`${baseURL}/api/admin/upload`, {
          method: 'POST',
          headers: token ? { 'x-admin-token': token } : {},
          body: fd
        });
        const data = await res.json();
        if (data.url) {
          const range = quill.getSelection(true);
          quill.insertEmbed(range.index, 'image', data.url);
          quill.setSelection(range.index + 1);
        } else {
          alert(data.error || '上传失败');
        }
      } catch (e) {
        alert('上传失败');
      }
    };
    input.click();
  };

  const modules = useMemo(() => ({
    toolbar: {
      container: [
        [{ header: [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ color: [] }, { background: [] }],
        [{ list: 'ordered' }, { list: 'bullet' }],
        [{ indent: '-1' }, { indent: '+1' }],
        ['link', 'image'],
        ['clean']
      ],
      handlers: { image: imageHandler }
    }
  }), []);
  const [dataLoaded, setDataLoaded] = useState(!id);
  const [form, setForm] = useState({
    title: '',
    description: '',
    cover_image: '',
    start_time: '',
    end_time: '',
    signup_deadline: '',
    location_name: '',
    location_address: '',
    location_guide: '',
    total_slots: 50,
    status: 'draft',
    basePrice: 99,
    earlyBirdLimit: 20,
    earlyBirdPrice: 69
  });

  const quillRef = useRef(null);
  const [coverPreview, setCoverPreview] = useState(''); // 从描述中提取的第一张图，用于预览

  useEffect(() => {
    if (isEdit) {
      setDataLoaded(false);
      adminRequest({ url: '/api/admin/activities', method: 'GET' })
        .then((res) => {
          const a = (res.data || []).find((x) => x.id === parseInt(id, 10));
          if (a) {
            const pr = a.pricing_rules || {};
            const base = (pr.basePrice || 9900) / 100;
            const rule = (pr.rules || [])[0] || {};
            const desc = a.description || '';
            setForm({
              title: a.title || '',
              description: desc,
              cover_image: a.cover_image || '',
              start_time: a.start_time ? a.start_time.slice(0, 16) : '',
              end_time: a.end_time ? a.end_time.slice(0, 16) : '',
              signup_deadline: a.signup_deadline ? a.signup_deadline.slice(0, 16) : '',
              location_name: a.location_name || '',
              location_address: a.location_address || '',
              location_guide: a.location_guide || '',
              total_slots: a.total_slots || 50,
              status: a.status || 'draft',
              basePrice: base,
              earlyBirdLimit: rule.limit || 20,
              earlyBirdPrice: (rule.price || 6900) / 100
            });
            setCoverPreview(extractFirstImageSrc(desc) || '');
            setDataLoaded(true);
          }
        })
        .catch(() => navigate('/admin'));
    }
  }, [id, isEdit, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: name.includes('Price') || name === 'total_slots' || name === 'earlyBirdLimit' ? (name === 'total_slots' || name === 'earlyBirdLimit' ? parseInt(value, 10) || 0 : parseFloat(value) || 0) : value }));
  };

  const handleDescriptionChange = (content) => {
    setCoverPreview(extractFirstImageSrc(content) || '');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const editor = quillRef.current?.getEditor?.();
    const description = editor ? editor.root.innerHTML : form.description;
    setLoading(true);
    try {
      const pricing = {
        basePrice: Math.round((form.basePrice || 99) * 100),
        rules: [{ type: 'early_bird_by_count', limit: form.earlyBirdLimit || 20, price: Math.round((form.earlyBirdPrice || 69) * 100) }]
      };
      const firstImg = extractFirstImageSrc(description);
      // base64 data URL 超长，不存入 cover_image；展示时从 description 提取
      const coverImage = firstImg && !firstImg.startsWith('data:') && firstImg.length <= 2000 ? firstImg : null;
      const body = {
        title: form.title,
        description,
        cover_image: coverImage,
        start_time: form.start_time,
        end_time: form.end_time,
        signup_deadline: form.signup_deadline || form.start_time,
        location_name: form.location_name,
        location_address: form.location_address,
        location_guide: form.location_guide,
        total_slots: form.total_slots,
        status: form.status,
        pricing_rules: pricing
      };
      if (isEdit) {
        await adminRequest({ url: `/api/admin/activities/${id}`, method: 'PUT', data: body });
        alert('更新成功');
      } else {
        await adminRequest({ url: '/api/admin/activities', method: 'POST', data: body });
        alert('创建成功');
      }
      navigate('/admin/activities');
    } catch (err) {
      alert(err.response?.data?.error || '保存失败');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = { width: '100%', padding: 10, marginBottom: 12, border: '1px solid #ddd', borderRadius: 6, boxSizing: 'border-box' };
  const labelStyle = { display: 'block', marginBottom: 4, fontWeight: 500, fontSize: 14 };

  return (
    <div style={{ maxWidth: 700 }}>
      <h2>{isEdit ? '编辑活动' : '新建活动'}</h2>
      <form onSubmit={handleSubmit} style={{ background: '#fff', padding: 24, borderRadius: 12 }}>
        <label style={labelStyle}>活动标题 *</label>
        <input name="title" value={form.title} onChange={handleChange} required style={inputStyle} />

        <label style={labelStyle}>活动描述（富文本）</label>
        <p style={{ color: '#64748b', fontSize: 12, margin: '-4px 0 8px' }}>封面将自动使用描述中的第一张图片</p>
        <div style={{ marginBottom: 16 }}>
          {dataLoaded ? (
            <ReactQuill
              key={id || 'new'}
              ref={quillRef}
              theme="snow"
              defaultValue={form.description || ''}
              onChange={handleDescriptionChange}
              modules={modules}
              style={{ minHeight: 200 }}
            />
          ) : (
            <div style={{ minHeight: 200, padding: 12, background: '#f5f5f5', borderRadius: 6, color: '#999' }}>
              加载中...
            </div>
          )}
        </div>

        <div style={{ marginBottom: 20, padding: '16px', background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
          <label style={{ ...labelStyle, marginBottom: 8 }}>封面预览（取自描述第一张图）</label>
          {coverPreview ? (
            <img
              src={coverPreview.startsWith('http') ? coverPreview : `${baseURL}${coverPreview}`}
              alt="封面预览"
              style={{ maxWidth: 300, maxHeight: 168, objectFit: 'cover', borderRadius: 8, border: '1px solid #ddd', display: 'block' }}
            />
          ) : (
            <div style={{ padding: 24, color: '#94a3b8', fontSize: 14, textAlign: 'center' }}>
              在活动描述中插入图片后，第一张将作为封面
            </div>
          )}
        </div>
        <label style={labelStyle}>开始时间 *</label>
        <input name="start_time" type="datetime-local" value={form.start_time} onChange={handleChange} required style={inputStyle} />
        <label style={labelStyle}>结束时间 *</label>
        <input name="end_time" type="datetime-local" value={form.end_time} onChange={handleChange} required style={inputStyle} />
        <label style={labelStyle}>报名截止</label>
        <input name="signup_deadline" type="datetime-local" value={form.signup_deadline} onChange={handleChange} style={inputStyle} />
        <label style={labelStyle}>地点名称 *</label>
        <input name="location_name" value={form.location_name} onChange={handleChange} required style={inputStyle} />
        <label style={labelStyle}>详细地址 *</label>
        <input name="location_address" value={form.location_address} onChange={handleChange} required style={inputStyle} />
        <label style={labelStyle}>交通指引</label>
        <input name="location_guide" value={form.location_guide} onChange={handleChange} style={inputStyle} />
        <label style={labelStyle}>总名额（0=不限）</label>
        <input name="total_slots" type="number" value={form.total_slots} onChange={handleChange} min={0} style={inputStyle} />
        <label style={labelStyle}>基础价格（元）</label>
        <input name="basePrice" type="number" step={0.01} value={form.basePrice} onChange={handleChange} style={inputStyle} />
        <label style={labelStyle}>早鸟人数限制</label>
        <input name="earlyBirdLimit" type="number" value={form.earlyBirdLimit} onChange={handleChange} style={inputStyle} />
        <label style={labelStyle}>早鸟价（元）</label>
        <input name="earlyBirdPrice" type="number" step={0.01} value={form.earlyBirdPrice} onChange={handleChange} style={inputStyle} />
        {isEdit && (
          <>
            <label style={labelStyle}>状态</label>
            <select name="status" value={form.status} onChange={handleChange} style={inputStyle}>
              <option value="draft">草稿</option>
              <option value="published">已发布</option>
            </select>
          </>
        )}
        <div style={{ display: 'flex', gap: 12, marginTop: 24 }}>
          <button type="submit" disabled={loading} style={{ padding: '12px 24px', background: '#6366f1', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer' }}>
            {loading ? '保存中...' : '保存'}
          </button>
          <button type="button" onClick={() => navigate('/admin/activities')} style={{ padding: '12px 24px', background: '#eee', border: 'none', borderRadius: 8, cursor: 'pointer' }}>
            取消
          </button>
        </div>
      </form>
    </div>
  );
}

export default AdminActivityForm;
